import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute';
import RoleRoute from './components/RoleRoute';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import PublicProfile from './pages/PublicProfile';
import ResetPassword from './pages/ResetPassword';
import StaffDashboard from './pages/StaffDashboard';
import SuperuserDashboard from './pages/SuperuserDashboard';
import MerchantBankLinked from './pages/MerchantBankLinked';
import SuperuserSchoolPay from './pages/SuperuserSchoolPay';
import SuperuserAudit from './pages/SuperuserAudit';
import SuperuserSchoolPayStatement from './pages/SuperuserSchoolPayStatement';
import SuperuserUsersProgress from './pages/SuperuserUsersProgress';
import SuperuserUserManagement from './pages/SuperuserUserManagement';
import SuperuserAddPoints from './pages/SuperuserAddPoints';
import SuperuserIncentiveManagement from './pages/SuperuserIncentiveManagement';
import SuperuserSetupTaskRewards from './pages/SuperuserSetupTaskRewards';
import SuperuserTransactionManagement from './pages/SuperuserTransactionManagement';
import SuperuserTransactionDetail from './pages/SuperuserTransactionDetail';
import SuperuserBilling from './pages/SuperuserBilling';
import SuperuserBillingInvoice from './pages/SuperuserBillingInvoice';
import SuperuserOliveMerchants from './pages/SuperuserOliveMerchants';
import SuperuserPointMultipliers from './pages/SuperuserPointMultipliers';
import SuperuserCardDrawManagement from './pages/SuperuserCardDrawManagement';
import SuperuserRewardExclusions from './pages/SuperuserRewardExclusions';
import SuperuserCheckInLocations from './pages/SuperuserCheckInLocations';
import SuperuserDataPortal from './pages/SuperuserDataPortal';
import SuperuserTestFreeEntries from './pages/SuperuserTestFreeEntries';
import SuperuserReceiptReview from './pages/SuperuserReceiptReview';
import SuperuserWildfireMerchants from './pages/SuperuserWildfireMerchants';
import SuperuserAccessPulls from './pages/SuperuserAccessPulls';
import SuperuserManageLinks from './pages/SuperuserManageLinks';
import SuperuserDocsManager from './pages/SuperuserDocsManager';
import SuperuserLinkGenerator from './pages/SuperuserLinkGenerator';
import StaffRewards from './pages/StaffRewards';
import StaffFans from './pages/StaffFans';
import StaffTransactions from './pages/StaffTransactions';
import StaffAnalytics from './pages/StaffAnalytics';
import StaffRaffles from './pages/StaffRaffles';
import SuperuserChallengePresets from './pages/SuperuserChallengePresets';
import SuperuserSchoolChallenges from './pages/SuperuserSchoolChallenges';
import StaffInitialRewards from './pages/StaffInitialRewards';
import StaffFulfillment from './pages/StaffFulfillment';
import StaffAuctions from './pages/StaffAuctions';
import StaffWeeklyRewards from './pages/StaffWeeklyRewards';
import StaffOverallRewards from './pages/StaffOverallRewards';
import StaffNotifications from './pages/StaffNotifications';
import SuperuserAnalytics from './pages/SuperuserAnalytics';
import SuperuserInsights from './pages/SuperuserInsights';
import StaffContent from './pages/StaffContent';
import StaffSettings from './pages/StaffSettings';
import SuperuserSchoolManagement from './pages/SuperuserSchoolManagement';
import RaffleDraw from './pages/RaffleDraw';
import AuctionDetails from './pages/AuctionDetails';
import ExtensionDownload from './pages/ExtensionDownload';
import EmbeddingDocs from './pages/EmbeddingDocs';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import Contact from './pages/Contact';
import MerchantsPage from './pages/MerchantsPage';
import TimelinePage from './pages/TimelinePage';
import OnboardingPage from './pages/OnboardingPage';
import SchoolPortalPage from './pages/SchoolPortalPage';
import IdeasPage from './pages/IdeasPage';
import RewardSelectionPage from './pages/RewardSelectionPage';
import Rewards from './pages/Rewards';
import Walkthrough from './pages/Walkthrough';
import EmbedTesterPortal from './pages/EmbedTesterPortal';
import EmbedTesterRoute from './components/EmbedTesterRoute';
import apiService from './services/api';
import { getPortalPathForUser } from './utils/portalRouting';
import './styles/App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(apiService.isAuthenticated());

  useEffect(() => {
    // Listen for storage changes to update auth state
    const handleStorageChange = () => {
      setIsAuthenticated(apiService.isAuthenticated());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleAuthChange = () => {
    setIsAuthenticated(apiService.isAuthenticated());
  };

  return (
    <Router>
      <div className="app">
        <Routes>
          <Route
            path="/login/"
            element={isAuthenticated ? <Navigate to={getPortalPathForUser(apiService.getCurrentUser())} replace /> : <Login onAuthChange={handleAuthChange} />}
          />

          <Route
            path="/verify-email"
            element={<VerifyEmail />}
          />
          <Route
            path="/verify-email/"
            element={<VerifyEmail />}
          />
          <Route
            path="/mobile/verify-email"
            element={<VerifyEmail />}
          />
          <Route
            path="/mobile/verify-email/"
            element={<VerifyEmail />}
          />
          <Route
            path="/hc_prof/:fanId"
            element={<PublicProfile />}
          />
          <Route
            path="/hc_prof/:fanId/"
            element={<PublicProfile />}
          />
          <Route
            path="/mobile/u/:fanId"
            element={<PublicProfile />}
          />
          <Route
            path="/mobile/u/:fanId/"
            element={<PublicProfile />}
          />

          <Route
            path="/mobile/reset-password"
            element={<ResetPassword />}
          />
          <Route
            path="/mobile/reset-password/"
            element={<ResetPassword />}
          />
          <Route
            path="/reset-password"
            element={<ResetPassword />}
          />
          <Route
            path="/reset-password/"
            element={<ResetPassword />}
          />

          {/* Public: where Stripe returns a merchant after ACH bank setup. They
              have no Homecrowd account, so this must stay outside PrivateRoute. */}
          <Route
            path="/merchant/bank-linked"
            element={<MerchantBankLinked />}
          />
          <Route
            path="/merchant/bank-linked/"
            element={<MerchantBankLinked />}
          />

          <Route
            path="/embed-tester/"
            element={
              <EmbedTesterRoute>
                <EmbedTesterPortal />
              </EmbedTesterRoute>
            }
          />

          <Route
            path="/staff/dashboard/"
            element={
              <RoleRoute require="staff">
                <StaffDashboard />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/dashboard/"
            element={
              <RoleRoute require="superuser">
                <SuperuserDashboard />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/insights/"
            element={
              <RoleRoute require="superuser">
                <SuperuserInsights />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/links/"
            element={
              <RoleRoute require="superuser">
                <SuperuserManageLinks />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/link-generator/"
            element={
              <RoleRoute require="superuser">
                <SuperuserLinkGenerator />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/docs/"
            element={
              <RoleRoute require="superuser">
                <SuperuserDocsManager />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/users/"
            element={
              <RoleRoute require="superuser">
                <SuperuserUserManagement />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/users-progress/"
            element={
              <RoleRoute require="superuser">
                <SuperuserUsersProgress />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/add-points/:userId"
            element={
              <RoleRoute require="superuser">
                <SuperuserAddPoints />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/incentives/"
            element={
              <RoleRoute require="superuser">
                <SuperuserIncentiveManagement />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/setup-task-rewards/"
            element={
              <RoleRoute require="superuser">
                <SuperuserSetupTaskRewards />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/transactions/"
            element={
              <RoleRoute require="superuser">
                <SuperuserTransactionManagement />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/receipts/"
            element={
              <RoleRoute require="superuser">
                <SuperuserReceiptReview />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/transaction/:id"
            element={
              <RoleRoute require="superuser">
                <SuperuserTransactionDetail />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/analytics/"
            element={
              <RoleRoute require="superuser">
                <SuperuserAnalytics />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/olive-merchants/"
            element={
              <RoleRoute require="superuser">
                <SuperuserOliveMerchants />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/point-multipliers/"
            element={
              <RoleRoute require="superuser">
                <SuperuserPointMultipliers />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/card-draw/"
            element={
              <RoleRoute require="superuser">
                <SuperuserCardDrawManagement />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/reward-exclusions/"
            element={
              <RoleRoute require="superuser">
                <SuperuserRewardExclusions />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/check-in/"
            element={
              <RoleRoute require="superuser">
                <SuperuserCheckInLocations />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/data-portal/"
            element={
              <RoleRoute require="superuser">
                <SuperuserDataPortal />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/challenge-presets/"
            element={
              <RoleRoute require="superuser">
                <SuperuserChallengePresets />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/school-challenges/"
            element={
              <RoleRoute require="superuser">
                <SuperuserSchoolChallenges />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/test-free-entries/"
            element={
              <RoleRoute require="superuser">
                <SuperuserTestFreeEntries />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/wildfire-merchants/"
            element={
              <RoleRoute require="superuser">
                <SuperuserWildfireMerchants />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/access-pulls/"
            element={
              <RoleRoute require="superuser">
                <SuperuserAccessPulls />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/invoices/"
            element={
              <RoleRoute require="merchant_pay">
                <SuperuserBilling />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/invoices/:yearMonth/:merchantId/"
            element={
              <RoleRoute require="merchant_pay">
                <SuperuserBillingInvoice />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/school-pay/"
            element={
              <RoleRoute require="superuser">
                <SuperuserSchoolPay />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/school-pay/:yearMonth/:schoolId/"
            element={
              <RoleRoute require="superuser">
                <SuperuserSchoolPayStatement />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/audit/"
            element={
              <RoleRoute require="superuser">
                <SuperuserAudit />
              </RoleRoute>
            }
          />

          {/* Billing was this feature's first name; keep the links that went out
              under it working rather than 404ing a bookmark. */}
          <Route
            path="/superuser/billing/"
            element={<Navigate to="/superuser/invoices/" replace />}
          />
          <Route
            path="/superuser/billing/:yearMonth/:merchantId/"
            element={<Navigate to="/superuser/invoices/" replace />}
          />

          <Route
            path="/staff/rewards/"
            element={
              <RoleRoute require="staff">
                <StaffRewards />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/rewards"
            element={
              <RoleRoute require="staff">
                <StaffRewards />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/fans/"
            element={
              <RoleRoute require="staff">
                <StaffFans />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/transactions/"
            element={
              <RoleRoute require="staff">
                <StaffTransactions />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/analytics/"
            element={
              <RoleRoute require="staff">
                <StaffAnalytics />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/receipts/"
            element={
              <RoleRoute require="staff">
                <SuperuserReceiptReview scope="staff" />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/school-management"
            element={
              <RoleRoute require="superuser">
                <SuperuserSchoolManagement />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/initial-rewards/"
            element={
              <RoleRoute require="staff">
                <StaffInitialRewards />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/fulfillment/"
            element={
              <RoleRoute require="staff">
                <StaffFulfillment />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/raffles/"
            element={
              <RoleRoute require="staff">
                <StaffRaffles />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/raffles/:raffleId/draw/"
            element={
              <RoleRoute require="staff">
                <RaffleDraw />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/auctions/"
            element={
              <RoleRoute require="staff">
                <StaffAuctions />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/auctions/:auctionId/details/"
            element={
              <RoleRoute require="staff">
                <AuctionDetails />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/weekly-rewards/"
            element={
              <RoleRoute require="staff">
                <StaffWeeklyRewards />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/overall-rewards/"
            element={
              <RoleRoute require="staff">
                <StaffOverallRewards />
              </RoleRoute>
            }
          />

          <Route
            path="/superuser/notifications/"
            element={
              <RoleRoute require="superuser">
                <StaffNotifications />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/content/"
            element={
              <RoleRoute require="staff">
                <StaffContent />
              </RoleRoute>
            }
          />

          <Route
            path="/staff/settings/"
            element={
              <RoleRoute require="staff">
                <StaffSettings />
              </RoleRoute>
            }
          />

          <Route
            path="/extension-download/"
            element={<ExtensionDownload />}
          />

          <Route
            path="/docs/embedding/"
            element={<EmbeddingDocs />}
          />

          <Route
            path="/docs/embedding/:topic/"
            element={<EmbeddingDocs />}
          />

          <Route path="/early-access-overview/:schoolId" element={<Walkthrough />} />
          <Route path="/early-access-overview/:schoolId/" element={<Walkthrough />} />

          <Route
            path="/about/"
            element={<About />}
          />

          <Route
            path="/privacy-policy/"
            element={<PrivacyPolicy />}
          />

          <Route
            path="/terms-and-conditions/"
            element={<TermsAndConditions />}
          />

          <Route
            path="/support/"
            element={<Contact />}
          />

          <Route
            path="/merchants/:schoolId/"
            element={<MerchantsPage />}
          />

          <Route path="/onboarding/:schoolId" element={<SchoolPortalPage />} />
          <Route path="/onboarding/:schoolId/" element={<SchoolPortalPage />} />
          <Route path="/onboarding/:schoolId/timeline" element={<OnboardingPage />} />
          <Route path="/onboarding/:schoolId/timeline/" element={<OnboardingPage />} />

          <Route path="/resources/fan-access" element={<IdeasPage />} />
          <Route path="/resources/fan-access/" element={<IdeasPage />} />
          <Route path="/resources/fan-access/:schoolId" element={<IdeasPage />} />
          <Route path="/resources/fan-access/:schoolId/" element={<IdeasPage />} />

          <Route path="/reward-selection" element={<RewardSelectionPage />} />
          <Route path="/reward-selection/" element={<RewardSelectionPage />} />
          <Route path="/reward-selection/:schoolId" element={<RewardSelectionPage />} />
          <Route path="/reward-selection/:schoolId/" element={<RewardSelectionPage />} />

          <Route
            path="/rewards/"
            element={
              <PrivateRoute>
                <Rewards />
              </PrivateRoute>
            }
          />
          <Route
            path="/rewards"
            element={
              <PrivateRoute>
                <Rewards />
              </PrivateRoute>
            }
          />

          <Route
            path="/superuser/onboarding"
            element={
              <RoleRoute require="superuser">
                <TimelinePage />
              </RoleRoute>
            }
          />
          <Route
            path="/superuser/onboarding/"
            element={
              <RoleRoute require="superuser">
                <TimelinePage />
              </RoleRoute>
            }
          />

          <Route
            path="/"
            element={<Navigate to={isAuthenticated ? getPortalPathForUser(apiService.getCurrentUser()) : "/login/"} replace />}
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;